import '@/components/App.css';
import { Container } from '@customink/pigment-react';
import {
  useCreateProject,
  useDesktopBreakpoint,
  useEffectOnce,
  useProject,
} from '@customink/project-service-react-components';
import Cookies from 'cookies-js';
import { useEffect } from 'react';

import { OrderSummary } from './OrderSummary';
import OrderSubmit from './OrderSummary/OrderSubmit';
import { StickyButtonBar } from './StickyButtonBar';

import Cart from '@/components/Cart';
import { CartLoader } from '@/components/CartLoader';
import { EmptyCart } from '@/components/EmptyCart';
import { googleAnalyticsTag } from '@/components/vendors/googleAnalytics';
import { StickyBarProvider } from '@/contexts/StickyBarContext';
import {
  useCheckoutEnabled,
  useIsProjectSettled,
  useOnInitialCheckoutCycleLoad,
} from '@/hooks/Project';
import { useAppMetrics } from '@/hooks/useAppMetrics';
import { useOrderQuantityType } from '@/hooks/useOrderQuantityType';
import {
  PS_CART_REDIRECT_DATESTAMP,
  PS_CART_REDIRECT_FLAG_NAME,
  VOLUME_DISCOUNT_DATESTAMP,
  VOLUME_DISCOUNT_FLAG_NAME,
} from '@/utils/featureFlags';
import { fireEvent } from '@/utils/metrics';
import { signaler } from '@/utils/signaler';

export default function App() {
  const {
    data: project,
    isError: isProjectError,
    isSuccess: isProjectSuccess,
  } = useProject();
  const [
    createProject,
    { isSuccess: isCreateSuccess, isUninitialized: isCreationUninitialized },
  ] = useCreateProject();
  useAppMetrics();
  const isSettled = useIsProjectSettled();
  const isProjectLoaded = isProjectSuccess || isProjectError;
  const quantityType = useOrderQuantityType();
  const isCheckoutValid = useCheckoutEnabled();
  const isInitialCheckoutCycleLoad = useOnInitialCheckoutCycleLoad();

  // If the current project response is missing an ID, we need to create a new project.
  useEffect(() => {
    if (isProjectLoaded && !project?.id && isCreationUninitialized) {
      createProject();
    }
  }, [
    createProject,
    isCreateSuccess,
    isCreationUninitialized,
    isProjectLoaded,
    project?.id,
  ]);

  useEffect(() => {
    const bfcacheListener = (event: Event) => {
      const evt = event as PageTransitionEvent;
      if (evt.persisted) {
        window.location.reload();
      }
    };
    window.addEventListener('pageshow', bfcacheListener);
    return () => window.removeEventListener('pageshow', bfcacheListener);
  }, []);

  useEffect(() => {
    const flagValue = Cookies.get(`feature_${PS_CART_REDIRECT_FLAG_NAME}`);
    if (flagValue) {
      const action = `${PS_CART_REDIRECT_DATESTAMP} funnelcake ${PS_CART_REDIRECT_FLAG_NAME}`;
      const label = `${action} ${flagValue}`;
      fireEvent('testing', action, label);
    }
  }, []);

  useEffect(() => {
    const flagValue = signaler.featureFlag(VOLUME_DISCOUNT_FLAG_NAME);
    if (flagValue) {
      const action = `${VOLUME_DISCOUNT_DATESTAMP} team-purchase ${VOLUME_DISCOUNT_FLAG_NAME}`;
      const label = `${action} ${flagValue}`;
      fireEvent('testing', action, label);
    }
  }, []);

  useEffectOnce(
    () => {
      type BeginCartPayload = {
        event: string;
        cart_type: 'singles' | 'mixed' | 'bulk' | 'reorder';
        is_cart_valid: number;
        cart_source?: string;
      };
      const beginCartPayload: BeginCartPayload = {
        event: 'begin_cart',
        cart_type: quantityType,
        is_cart_valid: isCheckoutValid ? 1 : 0,
      };

      // if url specifies a cart_source query param, add it to the payload
      const url = new URL(window.location.href);
      const cartSource = url.searchParams.get('cart_source');
      if (cartSource) {
        beginCartPayload.cart_source = cartSource;
      }
      googleAnalyticsTag(beginCartPayload);
    },
    isInitialCheckoutCycleLoad,
    [isInitialCheckoutCycleLoad, isCheckoutValid],
  );

  const isDesktop = useDesktopBreakpoint();

  const hasItems: boolean = !!project?.items?.length && isSettled;
  const isEmpty: boolean = !project?.items?.length && isSettled;

  return (
    <div
      className="pje-App"
      data-testid="app"
      data-version={import.meta.env.APP_VERSION}
    >
      <Container className="pje-Container-overrides" maxWidth="xl">
        {!isSettled && <CartLoader />}
        {hasItems && (
          <StickyBarProvider>
            <div className="pje-App-contents" data-testid="app-contents">
              <section className="pje-section pje-section-order-summary">
                <OrderSummary />
              </section>
              <section className="pje-section pje-section-cart">
                <Cart />
              </section>
              {!isDesktop && (
                <StickyButtonBar>
                  <OrderSubmit />
                </StickyButtonBar>
              )}
            </div>
          </StickyBarProvider>
        )}
        {isEmpty && <EmptyCart />}
      </Container>
    </div>
  );
}
